// Tarife, Grenzen und Add-ons (Freemium). Die Stripe-Price-IDs kommen
// serverseitig aus der Umgebung (STRIPE_PRICE_<PLAN>_<MONTH|YEAR>,
// STRIPE_PRICE_SEAT_<PLAN>, STRIPE_PRICE_ADDON_<ADDON>).

export type PlanId = 'free' | 'plus' | 'business';

export type AddonId = 'speicher' | 'branding';

/** Grenzen je Tarif; null = unbegrenzt */
export type LimitKey = 'users' | 'businesses' | 'projects' | 'openTasks' | 'storageMb' | 'videoSeconds' | 'activeShares' | 'boardsPerProject';
export type Limits = Record<LimitKey, number | null>;

/**
 * Funktionen, die ein Tarif freischaltet. KI läuft immer mit dem eigenen
 * Schlüssel des Teams; der Tarif entscheidet nur, ob man ihn hinterlegen darf.
 */
export type FeatureKey = 'ki' | 'mcp' | 'mcpWrite' | 'webhooks' | 'branding';

/** Alle Merkmale in der Reihenfolge des Tarifvergleichs */
export const FEATURE_KEYS: FeatureKey[] = ['ki', 'mcp', 'mcpWrite', 'webhooks', 'branding'];

export interface PlanDef {
  id: PlanId;
  name: string;
  /** Kurzer Satz für die Auswahl */
  claim: string;
  limits: Limits;
  features: FeatureKey[];
  /** Preise in Euro (nur Web, nie in den Apps anzeigen) */
  price: { month: number; year: number; extraUser: number } | null;
}

export interface AddonDef {
  id: AddonId;
  name: string;
  description: string;
  minPlan: PlanId;
  /** Euro je Monat */
  price: number;
  limitBoost?: Partial<Limits>;
  feature?: FeatureKey;
}

export const TRIAL_DAYS = 14;
/** Tarif, den neue Teams zum Testen bekommen */
export const TRIAL_PLAN: PlanId = 'plus';

export const PLANS: Record<PlanId, PlanDef> = {
  free: {
    id: 'free',
    name: 'Free',
    claim: 'Für den Einstieg allein oder zu zweit',
    limits: { users: 3, businesses: 1, projects: 3, openTasks: 50, storageMb: 1024, videoSeconds: 120, activeShares: 3, boardsPerProject: 1 },
    features: [],
    price: null,
  },
  plus: {
    id: 'plus',
    name: 'Plus',
    claim: 'Für den Betrieb mit kleinem Team',
    limits: { users: 5, businesses: 1, projects: null, openTasks: null, storageMb: 25 * 1024, videoSeconds: 15 * 60, activeShares: null, boardsPerProject: null },
    features: ['ki', 'mcp'],
    price: { month: 19, year: 190, extraUser: 4 },
  },
  business: {
    id: 'business',
    name: 'Business',
    claim: 'Für mehrere Geschäfte und größere Teams',
    limits: { users: 15, businesses: null, projects: null, openTasks: null, storageMb: 100 * 1024, videoSeconds: 60 * 60, activeShares: null, boardsPerProject: null },
    features: ['ki', 'mcp', 'mcpWrite', 'webhooks', 'branding'],
    price: { month: 49, year: 490, extraUser: 3 },
  },
};

export const PLAN_ORDER: PlanId[] = ['free', 'plus', 'business'];

export const ADDONS: Record<AddonId, AddonDef> = {
  speicher: { id: 'speicher', name: 'Mehr Speicher', description: '100 GB zusätzlich für Fotos, Videos und Dateien', minPlan: 'plus', price: 9, limitBoost: { storageMb: 100 * 1024 } },
  branding: { id: 'branding', name: 'Branding', description: 'Eigenes Logo und Farben in Freigaben', minPlan: 'plus', price: 5, feature: 'branding' },
};

export function isPlanId(v: unknown): v is PlanId {
  return v === 'free' || v === 'plus' || v === 'business';
}

export function meetsPlan(plan: PlanId, min: PlanId): boolean {
  return PLAN_ORDER.indexOf(plan) >= PLAN_ORDER.indexOf(min);
}

/** Wirksame Grenzen aus Tarif, Add-ons und zugekauften Plätzen. */
export function effectiveLimits(plan: PlanId, addons: AddonId[] = [], extraSeats = 0): Limits {
  const limits = { ...PLANS[plan].limits };
  for (const a of addons) {
    for (const [k, v] of Object.entries(ADDONS[a]?.limitBoost ?? {}) as [LimitKey, number | null][]) {
      const cur = limits[k];
      limits[k] = v === null || cur === null ? null : cur + v;
    }
  }
  if (extraSeats > 0 && limits.users !== null) limits.users += extraSeats;
  return limits;
}

export function hasFeature(plan: PlanId, feature: FeatureKey, addons: AddonId[] = []): boolean {
  return PLANS[plan].features.includes(feature) || addons.some((a) => ADDONS[a]?.feature === feature);
}

/** Kleinster Tarif, der die Grenze aufhebt bzw. die Funktion enthält. */
export function planFor(need: { limit: LimitKey; wanted: number } | { feature: FeatureKey }): PlanId | null {
  for (const p of PLAN_ORDER) {
    if ('feature' in need) {
      if (PLANS[p].features.includes(need.feature)) return p;
    } else {
      const max = PLANS[p].limits[need.limit];
      if (max === null || need.wanted <= max) return p;
    }
  }
  return null;
}

/** Nutzung, gemessen an den Grenzen */
export type Usage = Record<Exclude<LimitKey, 'videoSeconds' | 'boardsPerProject'>, number>;

const LIMIT_TEXT: Record<LimitKey, (max: number) => string> = {
  users: (n) => `sind bis zu ${n} Mitglieder möglich`,
  businesses: (n) => (n === 1 ? 'ist ein Geschäft möglich' : `sind bis zu ${n} Geschäfte möglich`),
  projects: (n) => `sind bis zu ${n} Projekte möglich`,
  openTasks: (n) => `sind bis zu ${n} offene Aufgaben möglich`,
  storageMb: (n) => `sind ${formatStorage(n)} Speicher enthalten`,
  videoSeconds: (n) => `sind Videos bis ${Math.round(n / 60)} Minuten möglich`,
  activeShares: (n) => `sind bis zu ${n} aktive Freigaben möglich`,
  boardsPerProject: (n) => (n === 1 ? 'ist ein Board je Projekt möglich' : `sind bis zu ${n} Boards je Projekt möglich`),
};

export const FEATURE_TEXT: Record<FeatureKey, string> = {
  ki: 'KI mit eigenem Schlüssel',
  mcp: 'Die Verbindung mit KI-Assistenten',
  mcpWrite: 'Das Schreiben über KI-Assistenten und API-Schlüssel',
  webhooks: 'Webhooks an andere Systeme',
  branding: 'Eigenes Branding in Freigaben',
};

/** Beschreibung für Tarifvergleiche */
export const FEATURE_DETAIL: Record<FeatureKey, string> = {
  ki: 'Transkripte, Kurzfassungen und Inhaltspunkte mit eigenem Schlüssel',
  mcp: 'KI-Assistenten und API-Schlüssel lesen Aufgaben (MCP)',
  mcpWrite: 'KI-Assistenten und API-Schlüssel legen auch Aufgaben und Nachrichten an',
  webhooks: 'Webhooks melden neue Aufgaben und Nachrichten an andere Systeme',
  branding: 'Eigenes Logo und Farben in Freigaben',
};

/** Satz für den Hinweis an der Grenze, z. B. „Im Tarif Free sind bis zu 3 Projekte möglich.“ */
export function limitMessage(plan: PlanId, key: LimitKey, limits: Limits = PLANS[plan].limits): string {
  const max = limits[key];
  return max === null ? '' : `Im Tarif ${PLANS[plan].name} ${LIMIT_TEXT[key](max)}.`;
}

export function featureMessage(plan: PlanId, feature: FeatureKey): string {
  return `${FEATURE_TEXT[feature]} ist im Tarif ${PLANS[plan].name} nicht enthalten.`;
}

/** 1024 → „1 GB“, 25600 → „25 GB“, 350 → „350 MB“ */
export function formatStorage(mb: number): string {
  if (mb < 1024) return `${Math.max(0, Math.round(mb))} MB`;
  const gb = mb / 1024;
  return `${gb >= 10 ? Math.round(gb) : gb.toFixed(1).replace('.', ',').replace(',0', '')} GB`;
}

/** Fehlertext des Servers an einer Tarifgrenze beginnt damit; Clients zeigen dann den Hinweis. */
export const PLAN_ERROR_PREFIX = 'Tarif:';
